import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
    useCreateRoleMutation,
    useUpdateRoleByIdMutation,
    useGetPermissionCategoriesQuery,
    useGetPermissionsByRoleQuery,
} from "../../../../redux/services/settingsRoleApi";
import { ChevronDown, ChevronUp } from "lucide-react";
import { usePermission } from "../../../../hooks/usePermission";

export interface Role {
    role_name: string;
    description?: string;
    users?: number;
}

export interface Permission {
    id: string;
    name: string;
    description?: string;
}

interface AddRoleViewProps {
    roleDescription: string | null;
    roleToEditId?: string | null;
    isDefaultRole?: boolean;
    onClose: () => void;
    onSaved: () => void;
}

const toTitleCase = (str: string) =>
    str
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const AddRoleView: React.FC<AddRoleViewProps> = ({
    roleDescription,
    roleToEditId,
    isDefaultRole = false,
    onClose,
    onSaved,
}) => {
    const [roleName, setRoleName] = useState("");
    const [roleDesc, setRoleDesc] = useState("");
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [openSection, setOpenSection] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const { data: categoryData, isLoading: loadingCategories } =
        useGetPermissionCategoriesQuery();

    const { data: rolePermissionsData } = useGetPermissionsByRoleQuery(
        roleToEditId || "",
        { skip: !roleToEditId }
    );

    const [createRole, { isLoading: creating }] = useCreateRoleMutation();
    const [updateRole, { isLoading: updating }] = useUpdateRoleByIdMutation();
    const { hasPermission } = usePermission();

    // Prefill name, description, and selected permissions
    useEffect(() => {
        if (rolePermissionsData?.data?.data) {
            const role = rolePermissionsData.data.data;
            setRoleName(role.role_name || "");
            setRoleDesc(roleDescription || "");
            const ids = role.permissions?.map((p: any) => p.id) || [];
            setSelectedPermissions(ids);
        } else if (!roleToEditId) {
            setRoleName("");
            setRoleDesc("");
            setSelectedPermissions([]);
        }
    }, [rolePermissionsData, roleToEditId]);

    const toggleSection = (name: string) => {
        setOpenSection((prev) => (prev === name ? null : name));
    };

    const handleSave = async () => {
        if (!roleName.trim()) {
            toast.error("Role name is required");
            return;
        }

        const payload = {
            name: roleName.trim(),
            description: roleDesc.trim(),
            permission_ids: selectedPermissions.length ? selectedPermissions : [],
        };

        try {
            if (roleToEditId) {
                if (!hasPermission("update role")) {
                    toast.error("You don't have permission to update roles");
                    return;
                }
                await updateRole({ roleId: roleToEditId, data: payload }).unwrap();
                toast.success("Role updated successfully");
            } else {
                if (!hasPermission("create role")) {
                    toast.error("You don't have permission to create roles");
                    return;
                }
                await createRole(payload).unwrap();
                toast.success("Role created successfully");
            }
            onSaved();
            onClose();
        } catch (error: any) {
            console.error("Error saving role:", error);
            const errorMessage =
                error?.data?.message ||
                error?.error ||
                "Failed to save role. Please try again.";
            toast.error(errorMessage);
        }
    };

    if (loadingCategories) {
        return (
            <div className="flex items-center justify-center py-10 text-gray-500">
                Loading permissions...
            </div>
        );
    }

    const categories = categoryData?.data?.categories || [];

    const toggleAllInSection = (permissions: Permission[], checked: boolean) => {
        const ids = permissions.map((p) => p.id);
        if (checked) {
            // Add all that are not already selected
            setSelectedPermissions((prev) => Array.from(new Set([...prev, ...ids])));
        } else {
            // Remove all
            setSelectedPermissions((prev) => prev.filter((id) => !ids.includes(id)));
        }
    };

    return (
        <div ref={containerRef} className="bg-white border rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#143887] mb-4">
                {roleToEditId ? "Edit Role" : "Add New Role"}
            </h2>

            {/* name and description */}
            <div className="space-y-3 mb-6">
                <input
                    type="text"
                    placeholder="Role name"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    disabled={isDefaultRole}
                    className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B9C3DB] ${isDefaultRole ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
                />
                <input
                    type="text"
                    placeholder="Description"
                    value={roleDesc}
                    onChange={(e) => setRoleDesc(e.target.value)}
                    disabled={isDefaultRole}
                    className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B9C3DB] ${isDefaultRole ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
                />
            </div>

            <div className="bg-gray-100 rounded-lg p-5 mb-4 font-medium text-gray-800 flex items-center justify-between">
                <span>Assign Permission</span>
                {isDefaultRole && (
                    <span className="text-[11px] font-normal bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-full">
                        View only — default role permissions cannot be modified
                    </span>
                )}
            </div>

            {/* permissions */}
            <div className="space-y-6">
                {categories.map((section: any, idx: number) => {
                    const isOpen = openSection === section.name;
                    const allChecked =
                        section.permissions.length > 0 &&
                        section.permissions.every((p: Permission) => selectedPermissions.includes(p.id));

                    return (
                        <div key={section.id} className="w-full">
                            {idx > 0 && <div className="my-4 border-t border-gray-300" />}

                            {/* section header */}
                            <button
                                type="button"
                                onClick={() => toggleSection(section.name)}
                                className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition rounded"
                            >
                                <span className="font-medium text-gray-800">
                                    {toTitleCase(section.name)}
                                </span>
                                {isOpen ? (
                                    <ChevronUp className="h-5 w-5 text-gray-500" />
                                ) : (
                                    <ChevronDown className="h-5 w-5 text-gray-500" />
                                )}
                            </button>

                            {isOpen && <div className="my-2 border-b border-gray-300" />}

                            {/* Section Content */}
                            {isOpen && section.permissions.length > 0 && (
                                <div className="bg-white px-6 py-4 space-y-3 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                                    {/* Select All */}
                                    <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                                        <span className="text-sm font-medium text-gray-800">
                                            Select All
                                        </span>
                                        <input
                                            type="checkbox"
                                            checked={allChecked}
                                            disabled={isDefaultRole}
                                            onChange={(e) => {
                                                if (isDefaultRole) return;
                                                toggleAllInSection(section.permissions, e.target.checked);
                                            }}
                                            className={`w-4 h-4 accent-blue-600 ${isDefaultRole ? "cursor-not-allowed opacity-60" : ""}`}
                                        />
                                    </div>

                                    {section.permissions.map((perm: Permission) => {
                                        const isChecked = selectedPermissions.includes(perm.id);

                                        return (
                                            <div
                                                key={perm.id}
                                                className="flex items-start justify-between gap-2 border-b last:border-none border-gray-100 pb-2"
                                            >
                                                <div>
                                                    <p className="text-sm font-medium text-gray-800">
                                                        {toTitleCase(perm.name)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {perm.description === perm.name
                                                            ? `Allows the user to ${perm.name}`
                                                            : perm.description}
                                                    </p>
                                                </div>

                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={isDefaultRole}
                                                    onChange={() => {
                                                        if (isDefaultRole) return;
                                                        setSelectedPermissions((prev) =>
                                                            prev.includes(perm.id)
                                                                ? prev.filter((id) => id !== perm.id)
                                                                : [...prev, perm.id]
                                                        );
                                                    }}
                                                    className={`w-4 h-4 accent-blue-600 mt-1 ${isDefaultRole ? "cursor-not-allowed opacity-60" : ""}`}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* save and cancel buttons */}
            <div className="flex gap-3 justify-end mt-6">
                <button
                    onClick={onClose}
                    className="py-[10px] px-[20px] bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    type="button"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={creating || updating || !hasPermission(roleToEditId ? "update role" : "create role")}
                    title={
                        !hasPermission(roleToEditId ? "update role" : "create role")
                            ? `You don't have permission to ${roleToEditId ? "update" : "create"} roles`
                            : undefined
                    }
                    className={`py-[10px] px-[40px] rounded text-white ${creating || updating || !hasPermission(roleToEditId ? "update role" : "create role")
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-[#143887] hover:bg-[#0f2d6d]"
                    }`}
                    type="button"
                >
                    {roleToEditId ? "Update Role" : "Add Role"}
                </button>
            </div>
        </div>
    );
};

export default AddRoleView;
